import React from "react";
import reportFile from "./report.xlsx";
import {
  Download,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Award,
  Droplet,
  Home as HomeIcon,
} from "lucide-react";
import epartitionVideo from "./intro.mp4";

const LandingPage = ({ onStartPartition }) => {
  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header Bar - Government Theme - STICKY - RESPONSIVE */}
      <header className="fixed top-0 left-0 right-0 bg-[#0A5F38] text-white shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-2 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Actual Government of Sindh Logo */}
              <img
                src="logo3.png"
                alt="Government of Sindh"
                className="w-10 h-10 sm:w-16 sm:h-16 object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='45' fill='%23fff'/%3E%3Ctext x='50' y='45' font-size='12' text-anchor='middle' fill='%230A5F38' font-weight='bold'%3EGovt of%3C/text%3E%3Ctext x='50' y='60' font-size='12' text-anchor='middle' fill='%230A5F38' font-weight='bold'%3ESindh%3C/text%3E%3C/svg%3E";
                }}
              />
              <div>
                <div className="text-xs sm:text-sm font-light hidden sm:block">
                  Government of Sindh
                </div>
                <div className="text-xs sm:text-lg font-semibold">
                  <span className="hidden sm:inline">
                    Office of the Assistant Commissioner Jhudo
                  </span>
                  <span className="sm:hidden">AC Office Jhudo</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm sm:text-xl font-medium tracking-wide">
                E-Partition System
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - with top padding for sticky header */}
      <main className="pt-16 sm:pt-24">
        {/* Hero Section - WITH REFINED BADGE AND SUBHEADING - RESPONSIVE */}
        <section className="bg-gradient-to-br from-green-50 to-white py-8 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-12 items-start">
              {/* Left: Text Content */}
              <div>
                {/* Badge - Digital Governance Initiative */}
                <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full mb-3 sm:mb-4 border border-green-200">
                  <Award className="w-3 h-3 sm:w-4 sm:h-4 text-[#0A5F38] mr-1.5 sm:mr-2" />
                  <span className="text-xs sm:text-sm font-medium text-[#0A5F38]">
                    Digital Governance Initiative
                  </span>
                </div>

                {/* Main Heading */}
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-3 sm:mb-4">
                  Land Partition
                  <br />
                  Automation System
                </h1>

                {/* Subheading - Elegant styling */}
                <div className="mb-4 sm:mb-6">
                  <div className="inline-block">
                    <p className="text-sm sm:text-lg text-gray-700 font-medium border-l-4 border-[#0A5F38] pl-3 sm:pl-4 py-1">
                      Precision, Transparency & Equity in Revenue Administration
                    </p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                  Land partition proceedings involve complex allocations across
                  irrigated, barani and banjar land, each with different value,
                  possession and built-up structures. This system supports
                  revenue officers in achieving fair, equitable and transparent
                  partition through a computational engine.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <button
                    onClick={onStartPartition}
                    className="flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-[#0A5F38] text-white rounded-lg hover:bg-[#084d2d] transition-all shadow-lg font-semibold text-sm sm:text-base"
                  >
                    Start New Partition
                    <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <a
                    href={reportFile}
                    download="Tapedar_Report_Format.xlsx"
                    className="flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-[#0A5F38] border-2 border-[#0A5F38] rounded-lg hover:bg-green-50 transition-all font-semibold text-sm sm:text-base"
                  >
                    <Download className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline">
                      Tapedar Report Template
                    </span>
                    <span className="sm:hidden">Tapedar Report</span>
                  </a>
                </div>
              </div>

              {/* Right: Feature Card + Attribution */}
              <div className="space-y-4 sm:space-y-6">
                {/* Feature Card */}
                <div className="bg-gradient-to-br from-gray-50 to-green-50 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-5 sm:p-8 border-t-4 border-[#0A5F38]">
                  <h3 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-4 sm:mb-6">
                    Computational Allocation Engine
                  </h3>
                  <ul className="space-y-3 sm:space-y-4">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#0A5F38] mr-2 sm:mr-3 flex-shrink-0 mt-0.5 sm:mt-1" />
                      <span className="text-sm sm:text-base text-gray-700">
                        Grade-wise allocation (A1–C) with possession priority
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#0A5F38] mr-2 sm:mr-3 flex-shrink-0 mt-0.5 sm:mt-1" />
                      <span className="text-sm sm:text-base text-gray-700">
                        Possession & built-up area protection protocols
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#0A5F38] mr-2 sm:mr-3 flex-shrink-0 mt-0.5 sm:mt-1" />
                      <span className="text-sm sm:text-base text-gray-700">
                        Instant Excel final statement generation
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#0A5F38] mr-2 sm:mr-3 flex-shrink-0 mt-0.5 sm:mt-1" />
                      <span className="text-sm sm:text-base text-gray-700">
                        Audit-ready logic trail for judicial scrutiny
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Attribution Card - Integrated */}
                <div className="bg-gradient-to-br from-gray-50 to-green-50 rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg p-4 sm:p-6 border-l-4 border-[#C9A13F]">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="bg-[#0A5F38] rounded-full p-2 sm:p-3 flex-shrink-0">
                      <Award className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs font-semibold text-gray-600 uppercase mb-0.5 sm:mb-1">
                        System Developer
                      </p>
                      <p className="text-base sm:text-lg font-serif text-[#0A5F38] font-bold">
                        Ghazanfar Perdakh, PAS
                      </p>
                      <p className="text-[10px] sm:text-xs text-gray-600">
                        Assistant Commissioner • 50th CTP
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY THIS SYSTEM - MERGED CHALLENGE + SOLUTION - RESPONSIVE */}
        <section className="py-8 sm:py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-center text-gray-900 mb-6 sm:mb-12">
              Why This System?
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-12">
              {/* Left: The Problem */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-md sm:shadow-lg border-t-4 border-red-600">
                <div className="flex items-center mb-4 sm:mb-6">
                  <div className="bg-red-600 rounded-full p-2 sm:p-3 mr-3 sm:mr-4">
                    <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-900">
                    The Challenge
                  </h3>
                </div>

                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start">
                    <span className="text-red-600 font-bold mr-2 sm:mr-3">
                      ×
                    </span>
                    <span className="text-sm sm:text-base text-gray-700">
                      <strong>Prolonged pendency:</strong> Partition cases
                      remain unresolved because there is no structured,
                      category-wise framework to conclude them efficiently
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 font-bold mr-2 sm:mr-3">
                      ×
                    </span>
                    <span className="text-sm sm:text-base text-gray-700">
                      <strong>Inequitable outcomes:</strong> Stronger parties
                      often secure better land while weaker claimants end up
                      with inferior categories – creating lasting grievances
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 font-bold mr-2 sm:mr-3">
                      ×
                    </span>
                    <span className="text-sm sm:text-base text-gray-700">
                      <strong>Category ambiguity:</strong> A1, A2, B2, C classes
                      are rarely applied rigorously, leading to uneven
                      distribution and avoidable disputes
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 font-bold mr-2 sm:mr-3">
                      ×
                    </span>
                    <span className="text-sm sm:text-base text-gray-700">
                      <strong>Possession flashpoints:</strong> Built-up areas,
                      houses, and long-held plots become the centre of conflict
                      in almost every case due to unclear protection
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 font-bold mr-2 sm:mr-3">
                      ×
                    </span>
                    <span className="text-sm sm:text-base text-gray-700">
                      <strong>Lack of transparency:</strong> Decisions are
                      difficult to justify because there is no uniform,
                      systematic reproducible method behind the final allocation
                      and thus accusations of favouritism emerge
                    </span>
                  </li>
                </ul>
              </div>

              {/* Right: The Solution */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-md sm:shadow-lg border-t-4 border-[#0A5F38]">
                <div className="flex items-center mb-4 sm:mb-6">
                  <div className="bg-[#0A5F38] rounded-full p-2 sm:p-3 mr-3 sm:mr-4">
                    <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-900">
                    The Solution
                  </h3>
                </div>

                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#0A5F38] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">
                      <strong>Accelerated disposal:</strong> A clear, rule-based
                      engine shortens the life cycle of each case and reduces
                      unnecessary hearings
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#0A5F38] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">
                      <strong>Fair & equitable distribution:</strong>{" "}
                      Category-wise entitlements are safeguarded so every party
                      receives a balanced and rightful share of A1/A2/C land
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#0A5F38] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">
                      <strong>Uniform standards:</strong> One consistent logic
                      for all cases ensures integrity, reduces subjectivity, and
                      strengthens administrative credibility
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#0A5F38] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">
                      <strong>Possession-aware allocation:</strong> Existing
                      houses, structures , autaaqs and long possession are
                      accommodated within policy limits to minimise post-order
                      disputes
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#0A5F38] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">
                      <strong>Transparent & defendable:</strong> The method
                      behind each allocation is documented, explainable, and
                      strengthens the defensibility of the final order
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        {/* Video Container - YouTube Embed */}
        {/* Video Section */}
        <section className="py-8 sm:py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-5 sm:mb-10">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-serif font-bold text-gray-900 mb-2 sm:mb-4">
                See How It Works
              </h2>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
                A Digital Governance Initiative transforming land partition in
                Sindh — ensuring fairness through computation
              </p>
            </div>

            {/* Video Container - Constrained Width */}
            <div className="max-w-2xl mx-auto">
              <div className="relative rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden shadow-xl border border-gray-200 bg-gray-900">
                <div
                  className="relative w-full"
                  style={{ paddingBottom: "56.25%" }}
                >
                  <video
                    className="absolute top-0 left-0 w-full h-full object-contain"
                    controls
                    playsInline
                    preload="metadata"
                  >
                    <source src={epartitionVideo} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Land Grading System Table - RESPONSIVE */}
        <section className="py-8 sm:py-16 bg-gradient-to-br from-gray-50 to-green-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900 mb-2 sm:mb-4 text-center">
              Land Grading System
            </h2>
            <p className="text-center text-sm sm:text-base text-gray-600 mb-6 sm:mb-12 max-w-3xl mx-auto">
              Scientifically-derived grading hierarchy based on irrigation
              status, cultivation potential, and commercial value
            </p>

            {/* Mobile Card View */}
            <div className="block lg:hidden space-y-3">
              {/* A1 */}
              <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-[#0A5F38]">
                <div className="font-semibold text-[#0A5F38] mb-1">
                  A1 (Perennial Irrigated)
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  Best quality irrigated agricultural land
                </p>
                <div className="flex items-center text-xs text-gray-600">
                  <Droplet className="w-3 h-3 text-blue-500 mr-1" />
                  Water in both Rabi & Kharif seasons
                </div>
              </div>
              {/* A2 */}
              <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-[#0A5F38]">
                <div className="font-semibold text-[#0A5F38] mb-1">
                  A2 (Seasonal Irrigated)
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  Irrigated but only in one season
                </p>
                <div className="flex items-center text-xs text-gray-600">
                  <Droplet className="w-3 h-3 text-blue-400 mr-1" />
                  Water in only one season
                </div>
              </div>
              {/* A3 */}
              <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-[#0A5F38]">
                <div className="font-semibold text-[#0A5F38] mb-1">
                  A3 (Barani)
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  Land reliant on rainfall or uncertain water sources
                </p>
                <div className="flex items-center text-xs text-gray-600">
                  <Droplet className="w-3 h-3 text-blue-300 mr-1" />
                  Rain-fed, no reliable water source
                </div>
              </div>
              {/* B1 */}
              <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-[#C9A13F]">
                <div className="font-semibold text-[#C9A13F] mb-1">
                  B1 (Potential Cultivable)
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  Currently uncultivated but can be brought under cultivation
                </p>
                <p className="text-xs text-gray-600">
                  High potential; better than banjar
                </p>
              </div>
              {/* B2 */}
              <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-[#C9A13F]">
                <div className="font-semibold text-[#C9A13F] mb-1">
                  B2 (Banjar but Valuable)
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  Non-cultivable but commercially valuable
                </p>
                <p className="text-xs text-gray-600">
                  Valuable due to road access, location
                </p>
              </div>
              {/* B3 */}
              <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-[#C9A13F]">
                <div className="font-semibold text-[#C9A13F] mb-1">
                  B3 (Banjar, Low Value)
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  Non-cultivable, infertile, or saline land
                </p>
                <p className="text-xs text-gray-600">
                  Lowest economic value; used for balancing
                </p>
              </div>
              {/* C */}
              <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-purple-600">
                <div className="font-semibold text-purple-700 mb-1">
                  C (Residential / Built-up)
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  Houses, Autaaqs, built-up areas
                </p>
                <p className="text-xs text-gray-600">
                  Possession-priority category; not to be disturbed
                </p>
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto shadow-xl rounded-lg">
              <table className="min-w-full bg-white">
                <thead className="bg-[#0A5F38] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Category
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Description
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Water / Status
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Remarks
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {/* A1 */}
                  <tr className="hover:bg-green-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-[#0A5F38]">
                      A1 (Perennial Irrigated)
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Best quality irrigated agricultural land
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      <div className="flex items-center">
                        <Droplet className="w-4 h-4 text-blue-500 mr-2" />
                        Water available in both Rabi & Kharif seasons
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      Highest productivity; premium category; first priority in
                      distribution
                    </td>
                  </tr>

                  {/* A2 */}
                  <tr className="bg-gray-50 hover:bg-green-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-[#0A5F38]">
                      A2 (Seasonal Irrigated)
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Irrigated but only in one season
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      <div className="flex items-center">
                        <Droplet className="w-4 h-4 text-blue-400 mr-2" />
                        Water available in only one season
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      Good agricultural land but lower than A1; used to balance
                      shares
                    </td>
                  </tr>

                  {/* A3 */}
                  <tr className="hover:bg-green-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-[#0A5F38]">
                      A3 (Barani)
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Land reliant on rainfall or uncertain water sources
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      <div className="flex items-center">
                        <Droplet className="w-4 h-4 text-blue-300 mr-2" />
                        Rain-fed, no reliable water source
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      Lower yield than A1/A2 but better than B-class land. Forms
                      the middle tier of cultivable categories.
                    </td>
                  </tr>

                  {/* B1 */}
                  <tr className="bg-gray-50 hover:bg-yellow-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-[#C9A13F]">
                      B1 (Potential Cultivable)
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Currently uncultivated but can be brought under
                      cultivation
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      May have nearby source; requires effort/investment
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      High potential; better than banjar
                    </td>
                  </tr>

                  {/* B2 */}
                  <tr className="hover:bg-yellow-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-[#C9A13F]">
                      B2 (Banjar but Valuable)
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Non-cultivable but commercially valuable
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      No irrigation water
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      Valuable due to road access, location, or future
                      development potential
                    </td>
                  </tr>

                  {/* B3 */}
                  <tr className="bg-gray-50 hover:bg-yellow-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-[#C9A13F]">
                      B3 (Banjar, Low Value)
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Non-cultivable, infertile, or saline land
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      No irrigation, poor soil
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      Lowest economic value; used for balancing when higher
                      grades exhausted
                    </td>
                  </tr>

                  {/* C */}
                  <tr className="hover:bg-purple-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-purple-700">
                      C (Residential / Built-up)
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Houses, Autaaqs, built-up areas
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      Not agricultural
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      Possession-priority category; not to be disturbed lightly;
                      often bundled with adjacent A/B
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Partition Principles - More Concise - RESPONSIVE */}
        <section className="py-8 sm:py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900 mb-6 sm:mb-12 text-center">
              Core Partition Principles
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
              {/* Principle 1 */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 sm:p-6 rounded-lg sm:rounded-xl shadow-sm sm:shadow-md border-l-4 border-[#0A5F38]">
                <div className="text-2xl sm:text-3xl font-bold text-[#0A5F38] mb-1 sm:mb-3">
                  1
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
                  Total Share Equality
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  Every shareholder receives land matching their
                  record-of-rights share
                </p>
              </div>

              {/* Principle 2 */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-3 sm:p-6 rounded-lg sm:rounded-xl shadow-sm sm:shadow-md border-l-4 border-blue-600">
                <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-1 sm:mb-3">
                  2
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
                  Grade-wise Fairness
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  A-category land distributed proportionately; B/C used to
                  balance equity
                </p>
              </div>

              {/* Principle 3 */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 sm:p-6 rounded-lg sm:rounded-xl shadow-sm sm:shadow-md border-l-4 border-purple-600">
                <div className="text-2xl sm:text-3xl font-bold text-purple-600 mb-1 sm:mb-3">
                  3
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
                  Bundle Integrity
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  Built-up plots and adjacent land kept together to avoid
                  splitting houses
                </p>
              </div>

              {/* Principle 4 */}
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-3 sm:p-6 rounded-lg sm:rounded-xl shadow-sm sm:shadow-md border-l-4 border-amber-600">
                <div className="text-2xl sm:text-3xl font-bold text-amber-600 mb-1 sm:mb-3">
                  4
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
                  Possession Priority
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  Existing possession given priority, especially for residential
                  land
                </p>
              </div>

              {/* Principle 5 */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 p-3 sm:p-6 rounded-lg sm:rounded-xl shadow-sm sm:shadow-md border-l-4 border-red-600">
                <div className="text-2xl sm:text-3xl font-bold text-red-600 mb-1 sm:mb-3">
                  5
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
                  Transparent Outputs
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  Complete documentation showing who got what, where, and why
                </p>
              </div>

              {/* Empty space for symmetry */}
              <div className="hidden md:block"></div>
            </div>
          </div>
        </section>

        {/* CTA Section - RESPONSIVE */}
        <section className="py-10 sm:py-20 bg-gradient-to-r from-[#0A5F38] to-[#084d2d]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-white mb-4 sm:mb-6">
              Ready to Generate a Partition Plan?
            </h2>
            <p className="text-base sm:text-xl text-green-100 mb-6 sm:mb-8">
              For use by Revenue Officers (Pilot – Taluka Jhudo)
            </p>
            <button
              onClick={onStartPartition}
              className="inline-flex items-center px-8 sm:px-10 py-4 sm:py-5 bg-white text-[#0A5F38] rounded-lg hover:bg-gray-100 transition-all shadow-xl font-bold text-base sm:text-lg"
            >
              Start New Partition
              <ArrowRight className="ml-2 sm:ml-3 w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </section>
      </main>

      {/* Simple Static Footer - Green Theme */}
      <footer className="bg-[#0A5F38] py-2 sm:py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[10px] sm:text-xs text-center text-green-100">
            © 2025 Ac Office Jhudo, Mirpurkhas • gperdakh@gmail.com
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
